/**
 * @fileoverview Syndicator Showdown — Runner Row (v2)
 * @module components/race/RunnerRow
 *
 * Data-dense single row in the field list. Replaces the embedded ShareSlider
 * with a BUY button that opens the BuyModal at the SelectionPage level.
 *
 * WHAT IT SHOWS:
 *   Runner number · Name · Jockey · Janus v4 rating · Value vs price · BUY
 *
 * WHAT IT DOES NOT DO:
 *   No slider, no inline equity adjustment. All purchasing happens in BuyModal.
 */

import React, { useState } from 'react';
import { getValueBadgeMeta } from '../../utils/valueBadge';
import './RunnerRow.css';

// ─── Rating band → short label + CSS tier ─────────────────────────────────────
// Mirrors janus_rating_engine_v4's ratingBand output exactly.

const RATING_BAND_META = {
  DOMINANT:       { label: 'Dominant',        tier: 'dominant' },
  ELITE_CHANCE:   { label: 'Elite Chance',    tier: 'elite' },
  STRONG_CHANCE:  { label: 'Strong Chance',   tier: 'strong' },
  SOLID_HOPE:     { label: 'Solid Hope',      tier: 'solid' },
  EACH_WAY:       { label: 'Each-Way',        tier: 'eachway' },
  NEEDS_FAVOURS:  { label: 'Needs Luck',      tier: 'needsluck' },
  ROUGHIE:        { label: 'Long Shot',       tier: 'roughie' },
};

function getRatingBandMeta(band) {
  return RATING_BAND_META[band] || { label: '', tier: 'solid' };
}

// ─── Rating score bubble ───────────────────────────────────────────────────────

const ScoreBubble = ({ displayRating, ratingBand }) => {
  if (displayRating == null) return null;
  const { tier } = getRatingBandMeta(ratingBand);
  return (
    <div className={`ss-runner-score ss-runner-score--${tier}`}>
      {Math.round(displayRating)}
    </div>
  );
};

// ─── Value classification pill ────────────────────────────────────────────────
// Always renders the simplified 6-term player-facing badge (Unknown / Bargain /
// Value / Fair / Pricey / Risk) from value_engine_v1 — never the internal
// 8-way valueLabel, and never displayRating-derived.

const ValuePill = ({ valueBadge, valueBadgeMeta }) => {
  if (!valueBadge || valueBadge === 'UNKNOWN') return null;
  const meta = getValueBadgeMeta(valueBadge, valueBadgeMeta);
  return (
    <div
      className="ss-runner-value-pill"
      style={{ borderColor: meta.colour, color: meta.colour, background: meta.bg }}
      title={meta.label}
    >
      {meta.label}
    </div>
  );
};

// ─── Expandable rating detail panel (positives / negatives / risk / breakdown) ─

const RatingDetailPanel = ({ runner }) => {
  const {
    winProbability, fairOdds, confidence, ratingBand,
    positives = [], negatives = [], riskFlags = [], breakdown = [],
    valueIndex, generatedSharePrice, modelFairSharePrice, valueExplanation,
    p1, p2, p3,
  } = runner;

  return (
    <div className="ss-runner-detail-panel">
      <div className="ss-runner-detail-stats">
        <div className="ss-runner-detail-stat">
          <span className="ss-runner-detail-stat__label">Band</span>
          <span className="ss-runner-detail-stat__value">{getRatingBandMeta(ratingBand).label || '—'}</span>
        </div>
        <div className="ss-runner-detail-stat">
          <span className="ss-runner-detail-stat__label">Win Chance</span>
          <span className="ss-runner-detail-stat__value">
            {winProbability != null ? `${(winProbability * 100).toFixed(1)}%` : '—'}
          </span>
        </div>
        <div className="ss-runner-detail-stat">
          <span className="ss-runner-detail-stat__label">Fair Odds</span>
          <span className="ss-runner-detail-stat__value">
            {fairOdds != null ? `$${fairOdds.toFixed(2)}` : '—'}
          </span>
        </div>
        <div className="ss-runner-detail-stat">
          <span className="ss-runner-detail-stat__label">Confidence</span>
          <span className="ss-runner-detail-stat__value">
            {confidence != null ? `${Math.round(confidence * 100)}%` : '—'}
          </span>
        </div>
        {(p1 != null || p2 != null || p3 != null) && (
          <div className="ss-runner-detail-stat">
            <span className="ss-runner-detail-stat__label">Place Chance (1-2-3)</span>
            <span className="ss-runner-detail-stat__value">
              {[p1, p2, p3].map(p => p != null ? `${Math.round(p * 100)}%` : '—').join(' / ')}
            </span>
          </div>
        )}
        {generatedSharePrice != null && (
          <div className="ss-runner-detail-stat">
            <span className="ss-runner-detail-stat__label">Model Fair Price</span>
            <span className="ss-runner-detail-stat__value">
              {modelFairSharePrice != null ? `$${Math.round(modelFairSharePrice).toLocaleString('en-AU')}` : '—'}
            </span>
          </div>
        )}
        {valueIndex != null && (
          <div className="ss-runner-detail-stat">
            <span className="ss-runner-detail-stat__label">Value Index</span>
            <span className="ss-runner-detail-stat__value">{valueIndex.toFixed(2)}×</span>
          </div>
        )}
      </div>

      {valueExplanation && (
        <div className="ss-runner-detail-value-explanation">{valueExplanation}</div>
      )}

      {positives.length > 0 && (
        <div className="ss-runner-detail-list ss-runner-detail-list--positive">
          {positives.map((p, i) => <span key={i}>✓ {p}</span>)}
        </div>
      )}
      {negatives.length > 0 && (
        <div className="ss-runner-detail-list ss-runner-detail-list--negative">
          {negatives.map((n, i) => <span key={i}>✗ {n}</span>)}
        </div>
      )}
      {riskFlags.length > 0 && (
        <div className="ss-runner-detail-flags">
          {riskFlags.map((flag, i) => (
            <span key={i} className="ss-runner-risk-flag">{flag.replace(/_/g, ' ')}</span>
          ))}
        </div>
      )}
      {breakdown.length > 0 && (
        <div className="ss-runner-detail-breakdown">
          {breakdown.map((f, i) => (
            <div key={i} className="ss-runner-detail-breakdown-row" title={f.detail}>
              <span className="ss-runner-detail-breakdown-name">{f.factor}</span>
              <span className={`ss-runner-detail-breakdown-score ${!f.hasData ? 'ss-runner-detail-breakdown-score--nodata' : ''}`}>
                {f.score}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Equity badge (shown when player has bought equity) ────────────────────────

const EquityBadge = ({ equityPercent }) => {
  if (!equityPercent || equityPercent <= 0) return null;
  return (
    <div className="ss-runner-equity-badge">
      {equityPercent}%
    </div>
  );
};

// ─── RunnerRow ─────────────────────────────────────────────────────────────────

/**
 * @param {Object}   runner              — runner data from Firestore
 * @param {number}   runner.runnerNumber
 * @param {string}   runner.name
 * @param {string}   runner.jockey
 * @param {string}   runner.trainer
 * @param {number}   runner.barrier
 * @param {number}   runner.displayRating — 1–99 public rating from janus_rating_engine_v4 (null if unavailable)
 * @param {string}   runner.ratingBand    — DOMINANT | ELITE_CHANCE | STRONG_CHANCE | SOLID_HOPE | EACH_WAY | NEEDS_FAVOURS | ROUGHIE
 * @param {number}   runner.winProbability — race-relative win probability (0–1)
 * @param {number}   runner.fairOdds       — 1 / winProbability
 * @param {number}   runner.confidence     — 0–1 data-quality-adjusted confidence
 * @param {string}   runner.valueLabel     — internal 8-way value_engine_v1 label (debug/explain only)
 * @param {string}   runner.valueBadge     — player-facing badge: UNKNOWN | BARGAIN | VALUE | FAIR | PRICEY | RISK
 * @param {Object}   runner.valueBadgeMeta — { label, colour, bg } for runner.valueBadge
 * @param {string}   runner.valueExplanation — one-line human-readable reason for the value badge
 * @param {string[]} runner.riskFlags
 * @param {string[]} runner.positives
 * @param {string[]} runner.negatives
 * @param {Array}    runner.breakdown      — per-factor score/weight/detail
 * @param {string}   runner.status         — 'active' | 'Scratched' etc.
 *
 * @param {number}   baseSharePrice   — cost of 1% from calculateBaseSharePrice
 * @param {number}   maxOwnershipPct  — equity ceiling
 * @param {number}   currentEquity    — player's existing holding (0 if none)
 * @param {boolean}  isRaceLocked     — prevents buying/selling after race has jumped
 * @param {boolean}  isEntryLocked    — prevents buying/selling after entry is submitted
 *
 * @param {Function} onBuy            — () => void — opens BuyModal at parent level
 * @param {Function} onSell           — () => void — opens SellEquityModal (optional)
 */
export const RunnerRow = ({
  runner,
  baseSharePrice,
  maxOwnershipPct,
  currentEquity        = 0,
  isRaceLocked         = false,
  isEntryLocked        = false,
  hasPendingAllocation = false,
  onBuy,
  onSell,
}) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const isScratched  = runner.status?.toLowerCase?.() === 'scratched';
  const holdsEquity  = currentEquity > 0;
  const hasRatingDetail = runner.displayRating != null && (
    (runner.positives && runner.positives.length) ||
    (runner.negatives && runner.negatives.length) ||
    (runner.riskFlags && runner.riskFlags.length) ||
    (runner.breakdown && runner.breakdown.length) ||
    runner.valueExplanation
  );

  // Determine which action button to show:
  //   pre-submission  → always BUY (allows topping up existing holdings)
  //   post-submission + holds equity → SELL (10% commission mechanic)
  //   post-submission + no equity    → BUY  (redeploy sold funds, field cap enforced in BuyModal)
  //   race locked (jumped)           → lock icon only
  //   scratched                      → SCR indicator
  //   hasPendingAllocation           → always BUY (spending a refund, no sell penalty)
  let buttonMode;
  if (isScratched) {
    buttonMode = 'scratched';
  } else if (isRaceLocked) {
    buttonMode = 'locked';
  } else if (!isEntryLocked) {
    buttonMode = 'buy';           // pre-submission: always BUY, even on held runners
  } else if (holdsEquity && !hasPendingAllocation) {
    buttonMode = 'sell';          // post-submission + holding equity → SELL (unless in alloc mode)
  } else {
    buttonMode = 'buy';           // post-submission + no equity, or in scratch-alloc mode → BUY
  }

  return (
    <div className={`ss-runner-row-wrap ${isScratched ? 'ss-runner-row--scratched' : ''}`}>
      <div className="ss-runner-row">

        {/* Runner number */}
        <div className="ss-runner-num-col">
          <span className="ss-runner-num">{runner.runnerNumber}</span>
        </div>

        {/* Name + connections */}
        <div className="ss-runner-info-col">
          <span className="ss-runner-name">
            {runner.name}
            {isScratched && <span className="ss-runner-scratched-tag">SCR</span>}
          </span>
          <span className="ss-runner-connections">
            {runner.barrier ? `B${runner.barrier}` : ''}
            {runner.jockey   ? ` · ${runner.jockey}`   : ''}
            {runner.trainer  ? ` · ${runner.trainer}`  : ''}
          </span>
        </div>

        {/* Rating score + value classification */}
        <div
          className={`ss-runner-rating-col ${hasRatingDetail ? 'ss-runner-rating-col--clickable' : ''}`}
          onClick={hasRatingDetail ? () => setDetailOpen(o => !o) : undefined}
          title={hasRatingDetail ? 'Tap for rating breakdown' : ''}
        >
          <ScoreBubble displayRating={runner.displayRating} ratingBand={runner.ratingBand} />
          <ValuePill valueBadge={runner.valueBadge} valueBadgeMeta={runner.valueBadgeMeta} />
        </div>

        {/* Price per 1% */}
        <div className="ss-runner-price-col">
          <span className="ss-runner-price-label">$/1%</span>
          <span className="ss-runner-price">
            {baseSharePrice
              ? '$' + baseSharePrice.toLocaleString('en-AU')
              : '—'}
          </span>
        </div>

        {/* Current equity badge */}
        <EquityBadge equityPercent={currentEquity} />

        {/* Action button */}
        {buttonMode === 'sell' ? (
          <button
            className="ss-runner-buy-btn ss-runner-buy-btn--sell"
            onClick={onSell}
            type="button"
            aria-label={`Sell equity in ${runner.name}`}
          >
            Sell
          </button>
        ) : buttonMode === 'buy' ? (
          <button
            className="ss-runner-buy-btn"
            onClick={onBuy}
            type="button"
          >
            BUY
          </button>
        ) : buttonMode === 'scratched' ? (
          <div className="ss-runner-buy-btn ss-runner-buy-btn--scratched">SCR</div>
        ) : (
          <div className="ss-runner-buy-btn ss-runner-buy-btn--locked">🔒</div>
        )}
      </div>

      {detailOpen && hasRatingDetail && <RatingDetailPanel runner={runner} />}
    </div>
  );
};

export default RunnerRow;
